import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import './ReportsHub.css';

interface QuickStat {
    label: string;
    value: string | number;
    icon: string;
    color: string;
    change?: string;
}

const ReportsHub: React.FC = () => {
    const [quickStats, setQuickStats] = useState<QuickStat[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchQuickStats();
    }, []);

    const fetchQuickStats = async () => {
        try {
            // Fetch quick statistics from various endpoints
            const [studentsRes, classesRes] = await Promise.all([
                axios.get('/Branches/students/'),
                axios.get('/Branches/classes/'),
            ]);

            setQuickStats([
                {
                    label: 'Wadarta Ardayda',
                    value: studentsRes.data.count || studentsRes.data.results?.length || 0,
                    icon: '👨‍🎓',
                    color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                },
                {
                    label: 'Wadarta Fasallada',
                    value: classesRes.data.length || 0,
                    icon: '🏫',
                    color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                },
                {
                    label: 'Warbixinada La Abuuray',
                    value: '5+',
                    icon: '📊',
                    color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                },
                {
                    label: 'Xogta Tooska Ah',
                    value: 'Live',
                    icon: '📈',
                    color: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                },
            ]);
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const reports = [
        {
            title: 'Warbixinta Ardayda',
            description: 'Xog dhameystiran oo ku saabsan ardayda oo ay ku jiraan lacagaha, dhibcaha, iyo xaadirinta',
            icon: '📚',
            path: '/student-report',
            color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            features: ['Macluumaadka Shaqsiga', 'Faahfaahinta Lacagta', 'Natiijooyinka Imtixaanka', 'Diiwaanka Xaadirinta'],
        },
        {
            title: 'Warbixinta Xaadirinta',
            description: 'La soco oo falanqee qaabka xaadirinta ardayda, fasalka, qeybta, iyo taariikhda',
            icon: '📅',
            path: '/attendance-report',
            color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            features: ['La Socodka Maalinlaha', 'Ardayda Maqan', 'Dhoofinta Xogta', 'Shaandhayn Kala Duwan'],
        },
        {
            title: 'Warbixinta Lacagaha',
            description: 'La soco lacagaha aan la bixin iyo heerka lacag bixinta ee fasallada oo dhan',
            icon: '💰',
            path: '/fees-report',
            color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            features: ['Lacagaha Aan La Bixin', 'Shaandhaynta Bishii', 'Fasal Ahaan', 'La Socodka Bixinta'],
        },
        {
            title: 'Warbixinta Diiwaangellinta',
            description: 'Eeg diiwaangellinta ardayda oo loo kala soocay fasal iyo taariikh',
            icon: '🎓',
            path: '/admission-report',
            color: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            features: ['Taariikhda', 'Shaandhaynta Fasalka', 'Faahfaahinta Ardayga', 'Dhoofinta Xogta'],
        },
        {
            title: 'Dakhliga & Kharashaadka',
            description: 'Guudmar maaliyadeed oo muujinaya dakhliga, kharashaadka, iyo faa\'iidada saafiga ah',
            icon: '💵',
            path: '/income-report',
            color: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            features: ['Burburinta Bishii', 'La Socodka Mushaarka', 'Dakhliga Saafiga', 'Shaandhaynta Sanadka'],
        },
    ];

    return (
        <div className="reports-hub-container">
            <div className="reports-hub-content">
                {/* Hero Section */}
                <div className="hero-section">
                    <div className="hero-content">
                        <h1 className="hero-title">
                            <span className="gradient-text">Warbixinada & Falanqaynta</span>
                        </h1>
                        <p className="hero-subtitle">
                            Xog dhameystiran iyo go'aanno xog-ogaal ah oo aad gacanta ku hayso
                        </p>
                    </div>
                    <div className="hero-animation">
                        <div className="floating-icon">📊</div>
                        <div className="floating-icon delay-1">📈</div>
                        <div className="floating-icon delay-2">💹</div>
                    </div>
                </div>

                {/* Quick Stats */}
                <div className="quick-stats-section">
                    <h2 className="section-title">Gaabsi Degdeg Ah</h2>
                    <div className="quick-stats-grid">
                        {loading ? (
                            <div className="loading-spinner">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            quickStats.map((stat, index) => (
                                <div
                                    key={index}
                                    className="stat-card"
                                    style={{ animationDelay: `${index * 0.1}s` }}
                                >
                                    <div className="stat-icon" style={{ background: stat.color }}>
                                        {stat.icon}
                                    </div>
                                    <div className="stat-details">
                                        <h3 className="stat-value">{stat.value}</h3>
                                        <p className="stat-label">{stat.label}</p>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Reports Grid */}
                <div className="reports-section">
                    <h2 className="section-title">Warbixinada La Heli Karo</h2>
                    <div className="reports-grid">
                        {reports.map((report, index) => (
                            <Link
                                key={index}
                                to={report.path}
                                className="report-card"
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                <div className="report-header" style={{ background: report.color }}>
                                    <span className="report-icon">{report.icon}</span>
                                </div>
                                <div className="report-body">
                                    <h3 className="report-title">{report.title}</h3>
                                    <p className="report-description">{report.description}</p>
                                    <div className="report-features">
                                        {report.features.map((feature, idx) => (
                                            <span key={idx} className="feature-badge">
                                                ✓ {feature}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div className="report-footer">
                                    <span className="view-report-btn">
                                        Eeg Warbixinta <span className="arrow">→</span>
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>

                {/* Additional Features */}
                <div className="features-section">
                    <h2 className="section-title">Tilmaamaha Warbixinta</h2>
                    <div className="features-grid">
                        <div className="feature-item">
                            <div className="feature-icon">🖨️</div>
                            <h4>Diyaar U Ah Daabacaad</h4>
                            <p>Toos uga daabaco warbixino xirfadeysan biraawsarka</p>
                        </div>
                        <div className="feature-item">
                            <div className="feature-icon">📄</div>
                            <h4>Dhoofinta PDF</h4>
                            <p>Kala soo deg warbixinada qaab PDF ah</p>
                        </div>
                        <div className="feature-item">
                            <div className="feature-icon">📊</div>
                            <h4>Dhoofinta Excel</h4>
                            <p>U dhoofi xogta Excel si aad u falanqayso dheeraad ah</p>
                        </div>
                        <div className="feature-item">
                            <div className="feature-icon">🔍</div>
                            <h4>Shaandhayn Sare</h4>
                            <p>Ku shaandhee fasal, taariikh, qeyb, iyo in ka badan</p>
                        </div>
                        <div className="feature-item">
                            <div className="feature-icon">⚡</div>
                            <h4>Xogta Tooska Ah</h4>
                            <p>Mar walba waa mid cusub oo wadata macluumaad toos ah</p>
                        </div>
                        <div className="feature-item">
                            <div className="feature-icon">📱</div>
                            <h4>Naqshad La Jaanqaadaysa</h4>
                            <p>Si habsami leh ugu shaqeysa dhammaan aaladaha</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportsHub;
